import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Respuesta, Moneda, Cotizacion } from '../_models/shared/_index';
import { ErrorService } from './error.service';

@Injectable()
export class MonedaService {
	private getMonedasUrl = environment.rest + '/Moneda/GetMonedas';
	private getCotizacionesFromMonedaUrl =
		environment.rest + '/Moneda/GetCotizacionesFromMoneda';
	private getMonedaByIdIdiomaUrl =
		environment.rest + '/Moneda/GetMonedaByIdIdioma';
	private addMonedaUrl = environment.rest + '/Moneda/AddMoneda';
	private addCotizacionMonedaUrl =
		environment.rest + '/Moneda/addCotizacionMoneda';
	private editMonedaUrl = environment.rest + '/Moneda/EditMoneda';
	private editCotizacionMonedaUrl =
		environment.rest + '/Moneda/editCotizacionMoneda';
	private deleteMonedaUrl = environment.rest + '/Moneda/DeleteMoneda';
	private deleteCotizacionUrl =
		environment.rest + '/Moneda/DeleteCotizacionMoneda';
	private getMonedasTodasUrl = environment.rest + '/Moneda/GetMonedasTodas';
	private obtenerCotizacionesUrl =
		environment.rest + '/Moneda/ObtenerCotizaciones';
	private getMonedaByIdUrl = environment.rest + '/Moneda/GetMonedaById';
	private getClienteCotizacionUrl =
		environment.rest + '/Moneda/GetClienteCotizaciones';
	private copiarTodasCotizacionesUrl =
		environment.rest + '/Moneda/CopiarTodasCotizaciones';

	private options = {
		headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
		withCredentials: true,
	};

	constructor(private http: HttpClient, private errorService: ErrorService) {}

	getMonedas(traerTodas: boolean = false): Observable<Respuesta> {
		let urlMonedas: string = traerTodas
			? this.getMonedasTodasUrl
			: this.getMonedasUrl;
		return this.http.get<Respuesta>(urlMonedas, this.options).pipe(
			tap((res) => {
				this.errorService.CheckResponse(res);
			}),
			catchError(
				this.errorService.handleError<Respuesta>(`GET -> getMonedas`)
			)
		);
	}

	getCotizacionesFromMoneda(
		idMoneda: number,
		idCliente: number
	): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.getCotizacionesFromMonedaUrl,
				{ idMoneda, idCliente },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> getHijosFromMoneda`
					)
				)
			);
	}

	getMonedaByIdIdioma(
		idMoneda: number,
		idIdioma: number
	): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.getMonedaByIdIdiomaUrl,
				{ idMoneda, idIdioma },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> getMonedaByIdIdioma`
					)
				)
			);
	}

	addMoneda(moneda: Moneda): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(this.addMonedaUrl, { moneda }, this.options)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> addMoneda`
					)
				)
			);
	}

	addCotizacionMoneda(
		idMoneda: number,
		cotizaciones: Cotizacion[]
	): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.addCotizacionMonedaUrl,
				{ idMoneda: idMoneda, cotizaciones: cotizaciones },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> addCotizacionMoneda`
					)
				)
			);
	}

	getClienteCotizacion(
		idNegocio: number,
		idMoneda: number,
		fecha: Date
	): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.getClienteCotizacionUrl,
				{ idNegocio: idNegocio, idMoneda: idMoneda, fecha: fecha },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> GetClienteCotizaciones`
					)
				)
			);
	}

	deleteMoneda(idMoneda: number): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.deleteMonedaUrl,
				{ idMoneda: idMoneda },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> deleteMoneda`
					)
				)
			);
	}

	deleteCotizacion(cotizacion: Cotizacion): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.deleteCotizacionUrl,
				{ cotizacion: cotizacion },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> deleteCotizacion`
					)
				)
			);
	}

	editMoneda(moneda: Moneda): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(this.editMonedaUrl, { moneda }, this.options)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> editMoneda`
					)
				)
			);
	}

	editCotizacionMoneda(moneda: Moneda): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.editCotizacionMonedaUrl,
				{ moneda },
				this.options
			)
			.pipe(
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> editCotizacionMoneda`
					)
				)
			);
	}

	getMonedaById(idMoneda: number): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(this.getMonedaByIdUrl, { idMoneda }, this.options)
			.pipe(
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> editCotizacionMoneda`
					)
				)
			);
	}

	obtenerCotizaciones(idMoneda, fecha): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.obtenerCotizacionesUrl,
				{ idMoneda, fecha },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> obtenerCotizaciones`
					)
				)
			);
	}

	copiarTodasCotizaciones(
		idMoneda: number,
		idCliente: number,
		idsClientesDestino: number[]
	): Observable<Respuesta> {
		return this.http
			.post<Respuesta>(
				this.copiarTodasCotizacionesUrl,
				{ idMoneda, idCliente, idsClientesDestino },
				this.options
			)
			.pipe(
				tap((res) => {
					this.errorService.CheckResponse(res);
				}),
				catchError(
					this.errorService.handleError<Respuesta>(
						`POST -> copiarTodasCotizaciones`
					)
				)
			);
	}

	////////////////////////////////////////////////

	private handleError<T>(operation = 'operation', result?: T) {
		return (error: any): Observable<Respuesta> => {
			// TODO: send the error to remote logging infrastructure
			console.error(error); // log to console instead

			// TODO: better job of transforming error for user consumption
			console.log(`${operation} failed: ${error.message}`);

			// Let the app keep running by returning an empty result.
			return of(new Respuesta());
		};
	}
}
